import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { format, addDays } from 'date-fns';

interface DailyGoal {
  id: string;
  date: string;
  primary_task: string;
  secondary_tasks: string[];
  additional_tasks: string[];
  is_completed: boolean;
}

function DailyGoals() {
  const [currentWeeklyPlan, setCurrentWeeklyPlan] = useState<any>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [dailyGoal, setDailyGoal] = useState<DailyGoal>({
    id: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    primary_task: '',
    secondary_tasks: ['', ''],
    additional_tasks: [''],
    is_completed: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchCurrentWeeklyPlan();
  }, []);

  useEffect(() => {
    if (currentWeeklyPlan) {
      fetchDailyGoal(currentWeeklyPlan.id);
    }
  }, [currentWeeklyPlan, selectedDate]);

  const fetchCurrentWeeklyPlan = async () => {
    try {
      const { data: sprint } = await supabase
        .from('sprints')
        .select('*')
        .eq('status', 'in_progress')
        .single();

      if (sprint) {
        const currentWeek = Math.ceil(
          (new Date().getTime() - new Date(sprint.start_date).getTime()) / 
          (1000 * 60 * 60 * 24 * 7)
        );

        const { data: weeklyPlan } = await supabase
          .from('weekly_plans')
          .select('*')
          .eq('sprint_id', sprint.id)
          .eq('week_number', currentWeek)
          .single();

        if (weeklyPlan) {
          setCurrentWeeklyPlan(weeklyPlan);
          fetchDailyGoal(weeklyPlan.id);
        }
      }
    } catch (error: any) {
      if (error.code === 'PGRST116') {
        return;
      }
      console.error('Error:', error);
    }
  };

  const fetchDailyGoal = async (weeklyPlanId: string) => {
    const date = format(selectedDate, 'yyyy-MM-dd');
    
    try {
      const { data, error } = await supabase
        .from('daily_goals')
        .select('*')
        .eq('weekly_plan_id', weeklyPlanId)
        .eq('date', date)
        .maybeSingle();

      if (error) {
        console.error('Error fetching daily goal:', error);
        return;
      }

      if (data) {
        setDailyGoal(data);
      } else {
        setDailyGoal({
          id: '',
          date,
          primary_task: '',
          secondary_tasks: ['', ''],
          additional_tasks: [''],
          is_completed: false,
        });
      }
    } catch (error) {
      console.error('Error fetching daily goal:', error);
    }
  };

  const handleSecondaryTaskChange = (index: number, value: string) => {
    const newTasks = [...dailyGoal.secondary_tasks];
    newTasks[index] = value;
    setDailyGoal({ ...dailyGoal, secondary_tasks: newTasks });
  };

  const handleAdditionalTaskChange = (index: number, value: string) => {
    const newTasks = [...dailyGoal.additional_tasks];
    newTasks[index] = value;
    setDailyGoal({ ...dailyGoal, additional_tasks: newTasks });
  };

  const addAdditionalTask = () => {
    setDailyGoal({
      ...dailyGoal,
      additional_tasks: [...dailyGoal.additional_tasks, ''],
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    if (!currentWeeklyPlan) {
      setError('No active weekly plan found. Please create a weekly plan first.');
      setLoading(false);
      return;
    }

    try {
      const { error: saveError } = await supabase
        .from('daily_goals')
        .upsert({
          weekly_plan_id: currentWeeklyPlan.id,
          date: format(selectedDate, 'yyyy-MM-dd'),
          primary_task: dailyGoal.primary_task,
          secondary_tasks: dailyGoal.secondary_tasks.filter(task => task.trim() !== ''),
          additional_tasks: dailyGoal.additional_tasks.filter(task => task.trim() !== ''),
          is_completed: dailyGoal.is_completed,
        });

      if (saveError) throw saveError;

      alert('Daily goals saved successfully!');
    } catch (err: any) {
      console.error('Error:', err);
      setError('Failed to save daily goals. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (direction: 'today' | 'tomorrow') => {
    const newDate = direction === 'today' ? new Date() : addDays(new Date(), 1);
    setSelectedDate(newDate);
  };

  if (!currentWeeklyPlan) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">No active weekly plan found. Please create a weekly plan first.</p>
        <a
          href="/weekly-planning"
          className="inline-block mt-4 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700"
        >
          Create Weekly Plan
        </a>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold">Daily Goals</h1>
        <div className="flex gap-2">
          <button
            onClick={() => handleDateChange('today')}
            className={`px-4 py-2 rounded-md ${
              format(selectedDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd')
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 hover:bg-gray-200'
            }`}
          >
            Today
          </button>
          <button
            onClick={() => handleDateChange('tomorrow')}
            className={`px-4 py-2 rounded-md ${
              format(selectedDate, 'yyyy-MM-dd') === format(addDays(new Date(), 1), 'yyyy-MM-dd')
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 hover:bg-gray-200'
            }`}
          >
            Tomorrow
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-400 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Primary Task
            <textarea
              value={dailyGoal.primary_task}
              onChange={(e) => setDailyGoal({ ...dailyGoal, primary_task: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              rows={2}
              required
              disabled={loading}
            />
          </label>
        </div>

        <div className="space-y-4">
          <h2 className="text-lg font-medium">Secondary Tasks</h2>
          {dailyGoal.secondary_tasks.map((task, index) => (
            <div key={index}>
              <input
                type="text"
                value={task}
                onChange={(e) => handleSecondaryTaskChange(index, e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder={`Secondary task ${index + 1}`}
                required
                disabled={loading}
              />
            </div>
          ))}
        </div>

        <div className="space-y-4">
          <h2 className="text-lg font-medium">Additional Tasks</h2>
          {dailyGoal.additional_tasks.map((task, index) => (
            <div key={index}>
              <input
                type="text"
                value={task}
                onChange={(e) => handleAdditionalTaskChange(index, e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder={`Additional task ${index + 1}`}
                disabled={loading}
              />
            </div>
          ))}
          <button
            type="button"
            onClick={addAdditionalTask}
            className="text-sm text-blue-600 hover:text-blue-700"
            disabled={loading}
          >
            + Add Another Task
          </button>
        </div>

        <button
          type="submit"
          disabled={loading}
          className={`w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
            loading ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          {loading ? 'Saving...' : 'Save Daily Goals'}
        </button>
      </form>
    </div>
  );
}

export default DailyGoals;